/**
 * reservoirHealth.ts - Six-dimensional health assessment for a reservoir scenario.
 *
 *   1. Bed health        - ORP, SOD, bed shear, macrofaunal viability
 *   2. Oxygen budget     - DO surface / hypolimnion, depletion rate, days to anoxia
 *   3. Algae & T/O risk  - chl-a, cyano fraction, Kumagai amplifier, geosmin
 *   4. Nutrient regime   - N:P ratio, limitation, internal P release flux
 *   5. Compliance        - Mn, Fe, geosmin, 2-MIB vs DWI limits
 *   6. Operational       - OTE, -/kg O-, plume coverage proxy, supply-vs-demand
 *
 * Each dimension produces 0-100 score + traffic-light status + sub-metrics + action.
 * Inputs are the ScenarioInputs from scenarioManager.ts.
 */

import type { ScenarioInputs } from './scenarioManager';

export type HealthStatus = 'ok' | 'warn' | 'critical';

export interface HealthSubMetric {
  label: string;
  value: string;
  status: HealthStatus;
  target: string;
}

export interface HealthDimension {
  id: string;
  label: string;
  /** Single-line description of what this dimension covers. */
  blurb: string;
  /** 0-100 numeric score. */
  score: number;
  status: HealthStatus;
  /** 4-6 sub-metrics that contributed to the score. */
  subMetrics: HealthSubMetric[];
  /** One-line action if score is below 60. */
  action?: string;
  /** Modules in the train responsible for this dimension. */
  contributingModules: string[];
}

export interface ReservoirHealthAssessment {
  /** Aggregate score (weighted mean of dimensions). */
  overallScore: number;
  overallStatus: HealthStatus;
  /** Six dimensional scores. */
  dimensions: HealthDimension[];
  /** Top 3-5 prioritised actions. */
  topActions: string[];
}

// ------------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------------

const clamp = (x: number, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, x));

/** Linear score: returns 100 at idealValue, 0 at zeroValue, clamped 0-100. */
function linearScore(x: number, idealValue: number, zeroValue: number): number {
  if (idealValue === zeroValue) return 50;
  const t = (x - zeroValue) / (idealValue - zeroValue);
  return clamp(t * 100);
}

/** "Higher is better" - returns 100 at goodAt, 0 at badAt; piecewise linear. */
function highIsBetter(x: number, badAt: number, goodAt: number): number {
  if (x >= goodAt) return 100;
  if (x <= badAt) return 0;
  return ((x - badAt) / (goodAt - badAt)) * 100;
}

/** "Lower is better" - returns 100 at goodAt, 0 at badAt. */
function lowIsBetter(x: number, goodAt: number, badAt: number): number {
  if (x <= goodAt) return 100;
  if (x >= badAt) return 0;
  return ((badAt - x) / (badAt - goodAt)) * 100;
}

function statusOf(score: number): HealthStatus {
  if (score >= 75) return 'ok';
  if (score >= 50) return 'warn';
  return 'critical';
}

function subStatusFromScore(score: number): HealthStatus {
  return statusOf(score);
}

// ------------------------------------------------------------------------
// Dimensions
// ------------------------------------------------------------------------

/** 1. Bed health */
function assessBed(p: ScenarioInputs): HealthDimension {
  // Sub-scores
  const orpScore = highIsBetter(p.orp_mV, -100, 250);
  // SOD derived from NOM (Imai 1998)
  const tempFactor = Math.pow(1.07, p.hypoTempC - 20);
  const nomEnh = Math.min(3, 1 + 0.12 * p.nom_mgL);
  const sod = 1.4 * nomEnh * tempFactor;
  const sodScore = lowIsBetter(sod, 1.5, 3.5);
  // Bed shear (Mehta-Partheniades style, calibrated so Barcombe FULL 75 L/s
  // over 34 000 m^2 -> tau_b = 0.18 Pa). Q_total estimated from per-device flow
  // and default 1-per-1700 m^2 layout (Cooke 2005).
  const nDiffusers_bed = Math.max(8, Math.round(p.area_m2 / 1700));
  const Q_total_Ls = p.diffuserQair_Ls * nDiffusers_bed;
  const C_tau = 0.18 / Math.pow(75 / Math.sqrt(34000), 1.5);
  const tau_b = Q_total_Ls > 0
    ? C_tau * Math.pow(Q_total_Ls / Math.sqrt(p.area_m2), 1.5)
    : 0.02;
  const shearScore = lowIsBetter(tau_b, 0.08, 0.30);
  // Macrofaunal viability from DO at interface (Aller)
  const macroScore = highIsBetter(p.doHypo_mgL, 1, 5);
  // Methane proxy from anoxia
  const ch4Score = p.doHypo_mgL > 4 ? 100 : p.doHypo_mgL > 2 ? 70 : 25;

  const subMetrics: HealthSubMetric[] = [
    { label: 'Sediment ORP',
      value: `${p.orp_mV} mV`,
      status: subStatusFromScore(orpScore),
      target: '> + 250 mV (oxic)' },
    { label: 'Effective SOD',
      value: `${sod.toFixed(2)} g O-/m-/d`,
      status: subStatusFromScore(sodScore),
      target: '< 1.5 g O-/m-/d' },
    { label: 'Bed shear at modules',
      value: `${tau_b.toFixed(3)} Pa`,
      status: subStatusFromScore(shearScore),
      target: '< 0.08 Pa' },
    { label: 'Macrofaunal viability',
      value: `${p.doHypo_mgL.toFixed(1)} mg/L DO supports macrofauna`,
      status: subStatusFromScore(macroScore),
      target: '> 4 mg/L sustained' },
    { label: 'Methane risk',
      value: ch4Score > 70 ? 'low' : ch4Score > 40 ? 'moderate' : 'high',
      status: subStatusFromScore(ch4Score),
      target: 'low - bed kept oxic' },
  ];
  const score = clamp((orpScore + sodScore + shearScore + macroScore + ch4Score) / 5);

  return {
    id: 'bed', label: 'Bed Health',
    blurb: 'Sediment-water interface - ORP, SOD, bed shear, macrofaunal recovery potential',
    score, status: statusOf(score), subMetrics,
    action: score < 60 ? 'Drive interface DO > 4 mg/L by sustaining aeration; verify bed shear is below cohesive -_ce.' : undefined,
    contributingModules: ['boudreau_diagenesis', 'mehta_partheniades', 'aller_bioturbation', 'nom_sod', 'redox_p_release'],
  };
}

/** 2. Oxygen budget */
function assessOxygen(p: ScenarioInputs): HealthDimension {
  const tempFactor = Math.pow(1.07, p.hypoTempC - 20);
  const nomEnh = Math.min(3, 1 + 0.12 * p.nom_mgL);
  const sod = 1.4 * nomEnh * tempFactor;
  const hypoThickness = Math.max(1, p.depth_max_m - p.depth_mean_m);
  const depletionRate = sod / hypoThickness;
  const daysToAnoxia = p.doHypo_mgL / Math.max(depletionRate, 0.05);

  // Aerator supply margin. Default diffuser count: Cooke 2005 ~ 1 per 1700 m^2.
  // O2 mass: Q_air[L/s] x 0.001 (m3/L) x 1.225 (kg/m3 air) x 0.2315 (O2 mass frac)
  //          x OTE x 86400 (s/day).
  const nDiffusers = Math.max(8, Math.round(p.area_m2 / 1700));
  // OTE estimate via McGinnis at given depth + bubble size (matches strategyCYearRound)
  const kLa_perM_ox = 0.08 * (3 / Math.max(p.bubble_d_mm, 0.5));
  const tempFactor_ox = Math.pow(1.024, p.surfaceTempC - 20);
  const ote_ox = Math.min(0.85, 1 - Math.exp(-p.diffuserDepth_m * kLa_perM_ox * tempFactor_ox));
  const o2Supply_kgDay = nDiffusers * p.diffuserQair_Ls * 0.001 * 1.225 * 0.2315 * ote_ox * 86400;
  const o2Demand_kgDay = sod * p.area_m2 / 1000;
  const supplyMargin = o2Supply_kgDay / Math.max(o2Demand_kgDay, 0.01);

  const surfDoScore = highIsBetter(p.doSurface_mgL, 4, 8);
  const hypoDoScore = highIsBetter(p.doHypo_mgL, 1, 6);
  const depletionScore = lowIsBetter(depletionRate, 0.2, 0.5);
  const anoxiaScore = highIsBetter(daysToAnoxia, 14, 60);
  const marginScore = highIsBetter(supplyMargin, 1.0, 1.6);

  const subMetrics: HealthSubMetric[] = [
    { label: 'DO at surface',         value: `${p.doSurface_mgL.toFixed(1)} mg/L`,        status: subStatusFromScore(surfDoScore),  target: '> 8 mg/L' },
    { label: 'DO at sediment intf.', value: `${p.doHypo_mgL.toFixed(1)} mg/L`,           status: subStatusFromScore(hypoDoScore),  target: '> 4 mg/L sustained' },
    { label: 'O- depletion rate',    value: `${depletionRate.toFixed(2)} mg/L/d`,        status: subStatusFromScore(depletionScore), target: '< 0.2 mg/L/d' },
    { label: 'Days to anoxia',        value: `${daysToAnoxia.toFixed(0)} d`,              status: subStatusFromScore(anoxiaScore),  target: '> 60 d' },
    { label: 'Aerator supply margin', value: `- ${supplyMargin.toFixed(2)} of SOD demand`, status: subStatusFromScore(marginScore),  target: '> 1.5-' },
  ];
  const score = clamp((surfDoScore + hypoDoScore + depletionScore + anoxiaScore + marginScore) / 5);
  return {
    id: 'oxygen', label: 'Oxygen Budget',
    blurb: 'DO at surface & sediment interface, depletion rate, days-to-anoxia, supply vs demand margin',
    score, status: statusOf(score), subMetrics,
    action: score < 60 ? 'Increase Q_air per diffuser, add modules, or reduce SOD via NOM-management; target supply > 1.5- SOD.' : undefined,
    contributingModules: ['nom_sod', 'davison_fe_mn', 'mcginnis_bubble', 'cost_per_o2'],
  };
}

/** 3. Algae & T/O risk */
function assessAlgae(p: ScenarioInputs): HealthDimension {
  // Kumagai amplifier
  const A = p.stratified ? 1 + 0.6 * Math.tanh(Math.max(0, (p.chl_ugL - 10) / 15)) : 1;
  // Geosmin (Wang 2010)
  const gsm = 4.2 * p.chl_ugL * p.fCyano * (1 + 0.08 * (p.surfaceTempC - 15)) * (p.stratified ? 1.6 : 1.0);
  const mib = 2.8 * p.chl_ugL * p.fCyano * (1 + 0.06 * (p.surfaceTempC - 15)) * (p.stratified ? 1.4 : 1.0);

  const chlScore = lowIsBetter(p.chl_ugL, 15, 60);
  const cyanoScore = lowIsBetter(p.fCyano, 0.20, 0.70);
  const amplifierScore = lowIsBetter(A, 1.1, 1.7);
  const gsmScore = lowIsBetter(gsm, 10, 50);
  const mibScore = lowIsBetter(mib, 10, 50);

  const subMetrics: HealthSubMetric[] = [
    { label: 'Chlorophyll-a',         value: `${p.chl_ugL.toFixed(0)} -g/L`,       status: subStatusFromScore(chlScore),       target: '< 15 -g/L (WFD good)' },
    { label: 'Cyanobacterial fraction', value: `${(p.fCyano * 100).toFixed(0)} %`, status: subStatusFromScore(cyanoScore),     target: '< 20 % biovolume' },
    { label: 'Cyano-stratification amplifier (A)', value: `- ${A.toFixed(2)}`,                  status: subStatusFromScore(amplifierScore), target: '- 1.0 (no feedback)' },
    { label: 'Predicted geosmin',     value: `${gsm.toFixed(0)} ng/L`,             status: subStatusFromScore(gsmScore),       target: '< 10 ng/L (DWI)' },
    { label: 'Predicted 2-MIB',       value: `${mib.toFixed(0)} ng/L`,             status: subStatusFromScore(mibScore),       target: '< 10 ng/L (DWI)' },
  ];
  const score = clamp((chlScore + cyanoScore + amplifierScore + gsmScore + mibScore) / 5);
  return {
    id: 'algae', label: 'Algae & T/O Risk',
    blurb: 'Phytoplankton magnitude, cyanobacterial dominance, taste-and-odour metabolite prediction',
    score, status: statusOf(score), subMetrics,
    action: score < 60 ? 'Break stratification (aeration); target cyano < 25 %, chl-a < 30 -g/L summer mean.' : undefined,
    contributingModules: ['kumagai_feedback', 'csr_groups', 'np_colimitation', 'geosmin_predictor'],
  };
}

/** 4. Nutrient regime */
function assessNutrients(p: ScenarioInputs): HealthDimension {
  const L_N = p.tn_mgL / (p.tn_mgL + 0.10);
  const L_P = p.tp_mgL / (p.tp_mgL + 0.010);
  const npMolar = (p.tn_mgL / 14.01) / Math.max(p.tp_mgL / 30.97, 1e-9);
  const npRatio = npMolar;
  const regime  = npMolar > 22 ? 'P-limited' : npMolar < 10 ? 'N-limited' : 'co-limited';
  // Redox P release (Ma & Wu)
  const orpFrac = 0.5 * (1 - Math.tanh((p.orp_mV - 100) / 60));
  const pFlux = 12 * orpFrac;        // mg P/m-/d

  const tnScore = lowIsBetter(p.tn_mgL, 0.8, 3.0);
  const tpScore = lowIsBetter(p.tp_mgL, 0.040, 0.150);
  const fluxScore = lowIsBetter(pFlux, 2, 8);
  const ratioScore = regime === 'P-limited' ? 90 : regime === 'co-limited' ? 70 : 55;
  const limitScore = (L_N < 0.5 || L_P < 0.5) ? 70 : 50;

  const subMetrics: HealthSubMetric[] = [
    { label: 'TN',                       value: `${p.tn_mgL.toFixed(2)} mg/L`,        status: subStatusFromScore(tnScore),    target: '< 0.8 mg/L (WFD type-LM)' },
    { label: 'TP',                       value: `${(p.tp_mgL * 1000).toFixed(0)} -g/L`, status: subStatusFromScore(tpScore),  target: '< 20 -g/L' },
    { label: 'N:P (molar)',              value: `${npRatio.toFixed(1)} (${regime})`,  status: subStatusFromScore(ratioScore), target: '> 16 (Redfield) - P-limited' },
    { label: 'Sediment P release',       value: `${pFlux.toFixed(1)} mg/m-/d`,         status: subStatusFromScore(fluxScore),  target: '< 2 mg/m-/d (oxic)' },
    { label: 'Active limitation degree', value: `min(L_N, L_P) = ${Math.min(L_N, L_P).toFixed(2)}`, status: subStatusFromScore(limitScore), target: '< 0.5' },
  ];
  const score = clamp((tnScore + tpScore + ratioScore + fluxScore + limitScore) / 5);
  return {
    id: 'nutrients', label: 'Nutrient Regime',
    blurb: 'TN / TP load, N:P ratio limitation regime, ORP-driven internal P release flux',
    score, status: statusOf(score), subMetrics,
    action: score < 60 ? 'Aerate to oxidise interface and suppress internal P release; combine with catchment-P reduction.' : undefined,
    contributingModules: ['np_colimitation', 'reckhow_p_retention', 'redox_p_release', 'sediment_capping'],
  };
}

/** 5. Compliance */
function assessCompliance(p: ScenarioInputs): HealthDimension {
  const mnUgL = p.mnII_mgL * 1000;
  const feUgL = p.feII_mgL * 1000;
  const gsm = 4.2 * p.chl_ugL * p.fCyano * (1 + 0.08 * (p.surfaceTempC - 15)) * (p.stratified ? 1.6 : 1.0);
  const mib = 2.8 * p.chl_ugL * p.fCyano * (1 + 0.06 * (p.surfaceTempC - 15)) * (p.stratified ? 1.4 : 1.0);

  const mnScore = lowIsBetter(mnUgL, 50, 200);
  const feScore = lowIsBetter(feUgL, 200, 1000);
  const gsmScore = lowIsBetter(gsm, 10, 50);
  const mibScore = lowIsBetter(mib, 10, 50);
  const phScore = (p.pH >= 6.5 && p.pH <= 8.5) ? 100 : Math.max(0, 100 - Math.abs(p.pH - 7.5) * 30);

  const subMetrics: HealthSubMetric[] = [
    { label: 'Mn vs DWI 50 -g/L',  value: `${mnUgL.toFixed(0)} -g/L`,  status: subStatusFromScore(mnScore),  target: '< 50 -g/L (PCV)' },
    { label: 'Fe vs DWI 200 -g/L', value: `${feUgL.toFixed(0)} -g/L`,  status: subStatusFromScore(feScore),  target: '< 200 -g/L' },
    { label: 'Geosmin vs DWI 10 ng/L', value: `${gsm.toFixed(0)} ng/L`, status: subStatusFromScore(gsmScore), target: '< 10 ng/L' },
    { label: '2-MIB vs DWI 10 ng/L',   value: `${mib.toFixed(0)} ng/L`, status: subStatusFromScore(mibScore), target: '< 10 ng/L' },
    { label: 'pH within band',         value: `${p.pH.toFixed(1)}`,     status: subStatusFromScore(phScore),  target: '6.5-8.5' },
  ];
  const score = clamp((mnScore + feScore + gsmScore + mibScore + phScore) / 5);
  return {
    id: 'compliance', label: 'Regulatory Compliance',
    blurb: 'Mn, Fe, geosmin, 2-MIB vs UK DWI PCVs; pH within DrW operating band',
    score, status: statusOf(score), subMetrics,
    action: score < 60 ? 'Plan WTW PAC dosing or pre-treatment; aeration to suppress internal Mn/Fe release.' : undefined,
    contributingModules: ['davison_fe_mn', 'geosmin_predictor'],
  };
}

/** 6. Operational performance */
function assessOperational(p: ScenarioInputs): HealthDimension {
  // OTE approx via McGinnis at given depth + bubble size
  // OTE: McGinnis & Little 2002 / McGinnis 2006 simplified depth-resolved form.
  // Integration: rise column from release depth to surface, 1 m discretisation;
  // kLa scales by (3 / d_bubble); ASCE 2-06 (2007) Arrhenius theta = 1.024 ref 20 C.
  // Calibrated against McGinnis & Little 2002 Fig 6: 3 mm bubble at 4 m, T 19 C -> 27 percent.
  const kLa_perM = 0.08 * (3 / Math.max(p.bubble_d_mm, 0.5));
  const tempFactorOTE = Math.pow(1.024, p.surfaceTempC - 20);
  const oteEst = Math.min(0.85, 1 - Math.exp(-p.diffuserDepth_m * kLa_perM * tempFactorOTE));
  // -/kg O- proxy
  const costPerKgO2 = 0.45 / Math.max(oteEst, 0.05);
  // Stratification breakdown time inversely related to aeration intensity & N
  const stratBreakDays = p.stratified ? clamp(50 - 200 * (p.diffuserQair_Ls / Math.max(p.N_brunt * 1000, 1)), 3, 50) : 0;

  // Two distinct coverage metrics:
  // (a) Plume FOOTPRINT = sum of individual rise-plume cross-sections / basin area.
  //     Layout-density indicator: low = no plume merging (good); too high = overlap.
  // (b) Surface MIXING coverage = empirical influence area per diffuser scaled to basin.
  //     Derived from Cooke et al. (2005) "Restoration & Management of Lakes & Reservoirs"
  //     which documents 2000-5000 m^2 per fine-bubble diffuser at 4 m depth for shallow
  //     polymictic destratifiers, scaled by sqrt(depth) and sqrt(Q_air) per Asaeda-Imberger
  //     spread theory. Packing efficiency 0.65 accounts for hex-grid plume overlap.
  // Default 1 per 1700 m^2 (Cooke 2005), floor at 8 devices.
  const nDiffusers = Math.max(8, Math.round(p.area_m2 / 1700));
  const plumeRiseDiam = 0.34 * p.diffuserDepth_m * 6;
  const footprintArea = nDiffusers * Math.PI * Math.pow(plumeRiseDiam / 2, 2);
  const footprintFrac = Math.min(1, footprintArea / p.area_m2);

  // Cooke 2005 empirical: ~ 2500 m^2 influence area per device at 4 m / 2 L/s,
  // scaled by sqrt(depth/4) and sqrt(Q_air/2)
  const baseInfluenceArea_perDevice = 2500;
  const depthFactor = Math.pow(p.diffuserDepth_m / 4.0, 0.5);
  const qFactor     = Math.pow(p.diffuserQair_Ls / 2.0, 0.5);
  const influenceArea_perDevice = baseInfluenceArea_perDevice * depthFactor * qFactor;
  const surfaceCoverage = Math.min(1, nDiffusers * influenceArea_perDevice * 0.65 / p.area_m2);

  const oteScore = highIsBetter(oteEst, 0.10, 0.30);
  const costScore = lowIsBetter(costPerKgO2, 1.5, 4);
  const stratScore = p.stratified ? lowIsBetter(stratBreakDays, 10, 30) : 100;
  // Footprint score: ideal 1.5-15 % (avoid plume merging). High score for in-range.
  const footprintScore = (footprintFrac >= 0.015 && footprintFrac <= 0.15) ? 100
                        : footprintFrac < 0.015 ? 75
                        : footprintFrac < 0.30 ? 60 : 30;
  // Surface coverage: > 80 % target
  const surfaceCoverageScore = highIsBetter(surfaceCoverage, 0.40, 0.80);
  const bubbleScore = (p.bubble_d_mm >= 0.8 && p.bubble_d_mm <= 3.0) ? 100 : Math.max(0, 100 - Math.abs(p.bubble_d_mm - 2.0) * 25);

  const subMetrics: HealthSubMetric[] = [
    { label: 'Effective OTE',          value: `${(oteEst * 100).toFixed(0)} %`,    status: subStatusFromScore(oteScore),     target: '> 25 % (fine-bubble)' },
    { label: '- per kg O2 delivered',  value: `- ${costPerKgO2.toFixed(2)}`,        status: subStatusFromScore(costScore),    target: '< -1.5 / kg' },
    { label: 'Stratification breakdown', value: `${stratBreakDays.toFixed(0)} d`,   status: subStatusFromScore(stratScore),   target: '< 10 d' },
    { label: 'Surface mixing coverage', value: `${(surfaceCoverage * 100).toFixed(0)} %`, status: subStatusFromScore(surfaceCoverageScore), target: '> 80 % basin (industry benchmark)' },
    { label: 'Plume footprint (layout)', value: `${(footprintFrac * 100).toFixed(1)} %`,  status: subStatusFromScore(footprintScore),       target: '1.5-15 % (avoid merging)' },
    { label: 'Bubble release diameter', value: `${p.bubble_d_mm.toFixed(1)} mm`,    status: subStatusFromScore(bubbleScore),   target: '1.0-3.0 mm' },
  ];
  const score = clamp((oteScore + costScore + stratScore + surfaceCoverageScore + footprintScore + bubbleScore) / 6);
  return {
    id: 'operational', label: 'Operational Performance',
    blurb: 'Aeration system engineering performance and whole-life cost metrics',
    score, status: statusOf(score), subMetrics,
    action: score < 60 ? 'Switch to fine-bubble (1.5-2.5 mm), retune Q_air to 2.0 L/s/diffuser, hex-grid at 1.8x plume diameter; review surface mixing coverage target.' : undefined,
    contributingModules: ['mcginnis_bubble', 'cost_per_o2', 'asaeda_c', 'deep_zone', 'wind_tilt'],
  };
}

// ------------------------------------------------------------------------
// Public API
// ------------------------------------------------------------------------

const WEIGHTS = {
  bed: 0.20, oxygen: 0.20, algae: 0.20, nutrients: 0.15, compliance: 0.15, operational: 0.10,
};

export function assessReservoirHealth(p: ScenarioInputs): ReservoirHealthAssessment {
  const dimensions = [
    assessBed(p),
    assessOxygen(p),
    assessAlgae(p),
    assessNutrients(p),
    assessCompliance(p),
    assessOperational(p),
  ];
  const overallScore = clamp(
    dimensions[0].score * WEIGHTS.bed +
    dimensions[1].score * WEIGHTS.oxygen +
    dimensions[2].score * WEIGHTS.algae +
    dimensions[3].score * WEIGHTS.nutrients +
    dimensions[4].score * WEIGHTS.compliance +
    dimensions[5].score * WEIGHTS.operational
  );
  const overallStatus = statusOf(overallScore);

  // Top actions = ones from worst-scoring dimensions
  const topActions = dimensions
    .filter(d => d.action)
    .sort((a, b) => a.score - b.score)
    .slice(0, 5)
    .map(d => `${d.label}: ${d.action}`);

  return { overallScore, overallStatus, dimensions, topActions };
}
